import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { Button } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { Modal } from "../../components/Modal/Modal";
import { ProjectProvider, useProject } from "../../providers/ProjectProvider";
import { useFixedLocation } from "../../providers/RoutesProvider";
import { useRefresh } from "../../utils/hooks";
import { cn } from "../../utils/bem";
import { ProjectMetrics } from "./ProjectMetrics";
import { ReviewQueueBody } from "./ReviewQueue";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ProjectMetricsModal.prefix.css";

const block = cn("project-analytics-modal");

// Fullscreen modal that opens from the Data Manager's "Analytics"
// toolbar button. Two tabs: Review Queue (items flagged by the
// automated quality checks) and Metrics (the ProjectMetrics
// dashboard). Keeping both in one overlay consolidates what used
// to live in two places (DM toolbar → Metrics; Settings → Review
// Queue) so reviewers don't hop between routes to triage a project.
const Inner = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useFixedLocation();
  const modalRef = useRef();
  const refresh = useRefresh();
  const { project } = useProject();
  const api = useAPI();

  // Tab state persisted in the URL hash so a direct link / page
  // reload lands on the expected pane. Default to "review" per
  // the user's request (queue is the first thing a manager sees).
  const initialTab = (location.hash || "").replace(/^#/, "") === "metrics" ? "metrics" : "review";
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const newHash = activeTab === "metrics" ? "#metrics" : "#review";
    if (location.hash !== newHash) {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: newHash,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Live count of OPEN items in the review queue — rendered as a
  // small badge on the Review Queue tab so the manager sees "3"
  // without having to open the tab first. Mirrors the same badge
  // pattern used by the Notifications bell in the menubar.
  const [openCount, setOpenCount] = useState(null);
  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    api
      .callApi("projectReviewQueue", {
        params: { pk: project.id, status: "open", page_size: 1 },
        suppressError: true,
      })
      .then((res) => {
        if (cancelled) return;
        if (res && !res.error) {
          setOpenCount(res.status_counts?.open ?? res.count ?? 0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, project?.id]);

  const backToDM = useCallback(() => {
    const path = location.pathname.replace(ProjectMetricsModal.path, "");
    const search = location.search;
    const pathname = `${path}${search !== "?" ? search : ""}`;
    return refresh(pathname);
  }, [location, refresh]);

  const onClose = useCallback(() => {
    modalRef.current?.hide();
    backToDM();
  }, [backToDM]);

  return (
    <Modal ref={modalRef} onHide={backToDM} closeOnClickOutside={false} fullscreen visible bare>
      <Modal.Header divided>
        <div className={cn("modal").elem("title").toClassName()}>
          {project?.title ? `${project.title}. Analytics` : "Analytics"}
        </div>
        <Button
          size="small"
          look="outlined"
          onClick={onClose}
          aria-label={t("pages.settings.project_metrics_modal.close_analytics")}
        >
          Close
        </Button>
      </Modal.Header>

      {/* Horizontal tab strip. Styled to match the OrgSettings
          responsive tab row so operators see the same pattern as
          the rest of the Settings-style surfaces. */}
      <div className={block.elem("tabs").toClassName()} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "review"}
          className={block
            .elem("tab")
            .mod({ active: activeTab === "review" })
            .toClassName()}
          onClick={() => setActiveTab("review")}
        >
          Review Queue
          {openCount != null && openCount > 0 && (
            <span className={block.elem("badge").toClassName()}>{openCount > 99 ? "99+" : openCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "metrics"}
          className={block
            .elem("tab")
            .mod({ active: activeTab === "metrics" })
            .toClassName()}
          onClick={() => setActiveTab("metrics")}
        >
          Metrics
        </button>
      </div>

      <div className={block.elem("body").toClassName()} role="tabpanel">
        {activeTab === "review" ? (
          <ReviewQueueBody project={project} />
        ) : (
          /* ``embedded`` strips ProjectMetrics' page-level TabHeader so
             the modal's own "Metrics" tab label doesn't render under a
             second "Metrics" h2. Only the date-range picker survives. */
          <ProjectMetrics embedded />
        )}
      </div>
    </Modal>
  );
};

export const ProjectMetricsModal = () => (
  <ProjectProvider>
    <Inner />
  </ProjectProvider>
);

ProjectMetricsModal.path = "/metrics";
ProjectMetricsModal.modal = true;
